// src/components/Sidebar/Sidebar.jsx
import React, { useState } from "react";
import "./Sidebar.css";
import { useMediaQuery } from "react-responsive";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getImageSrc } from "../../utils/getImageSrc";
import ConfirmModal from "../modals/ConfirmModal/ConfirmModal";

const Sidebar = ({ isOpen, onToggle, onOpenNotifications }) => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const isDesktop = useMediaQuery({ minWidth: 992 });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = !!user;

  const displayName = user
    ? user.academyName || `${user.firstName} ${user.lastName}`
    : "Guest";

  const avatarSrc = getImageSrc(user?.picture);

  const userRole =
    user?.role === "institution"
      ? "Academy"
      : user?.role === "tutor"
        ? "Tutor"
        : user?.role === "student"
          ? "Student"
          : "";

  const homeRoute =
    user?.role === "student"
      ? "/student-home"
      : user?.role === "tutor"
        ? "/tutor-home"
        : user?.role === "institution"
          ? "/academy-home"
          : "/";

  const isStudent = userRole === "Student";

  // ==================== DESKTOP MENU ====================
  const desktopMenuItems = [
    { icon: "/images/home2.png", label: "Home", path: homeRoute },
    { icon: "/images/avatar3.png", label: "Account", path: "/account" },
    // ...(isStudent
    //   ? []
    //   : [
    //       {
    //         icon: "/images/subscription.png",
    //         label: "Subscriptions",
    //         path: "/subscriptions",
    //       },
    //     ]),
  ];

  // ==================== MOBILE MENU ====================
  const mobileMenuItems = isAuthenticated
    ? [
        { icon: "/images/home2.png", label: "Home", path: homeRoute },
        { icon: "/images/avatar3.png", label: "Account", path: "/account" },
        {
          icon: "/images/bell.png",
          label: "Notification",
          isNotification: true, // ← Special flag
        },
        // ...(isStudent
        //   ? []
        //   : [
        //       {
        //         icon: "/images/subscription.png",
        //         label: "Subscriptions",
        //         path: "/subscriptions",
        //       },
        //     ]),
        {
          icon: "/images/help-desk.png",
          label: "Help Desk",
          path: "/get-help",
        },
      ]
    : [];

  // ==================== ACTIVE ROUTE CHECKER ====================
  const isActive = (path) => {
    if (path === homeRoute) {
      return (
        location.pathname === homeRoute ||
        location.pathname.startsWith(homeRoute + "/")
      );
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const handleLogoutClick = () => setShowLogoutModal(true);

  const confirmLogout = () => {
    logout();
    setShowLogoutModal(false);
    navigate("/");
  };

  const cancelLogout = () => setShowLogoutModal(false);

  const AuthSection = () => {
    if (isAuthenticated) {
      return (
        <button className="logout-btn full" onClick={handleLogoutClick}>
          <img src="/images/logout.png" alt="Logout" />
          <span>Logout</span>
        </button>
      );
    }

    return (
      <div className="drawer-auth">
        <a href="/decision-screen" className="sidebar-button-primary">
          Create Account
        </a>
        <a href="/login" className="sidebar-login-btn">
          Login
        </a>
      </div>
    );
  };

  const TutorsLinksLogo = () => (
    <div className="tutorslinks-logo">
      <img src="/images/tutors-logo.png" alt="TutorsLinks" />
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      {isDesktop &&
      isAuthenticated &&
      !["/about-us", "/get-help"].includes(location.pathname) ? (
        <aside className="tutors-sidebar open">
          <nav className="sidebar-nav">
            {desktopMenuItems.map((item) => {
              const active = isActive(item.path);
              return (
                <button
                  key={item.label}
                  className={`nav-item ${active ? "active" : ""}`}
                  onClick={() => navigate(item.path)}
                >
                  <img src={item.icon} alt={item.label} className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="sidebar-bottom">
            <button className="logout-btn desktop" onClick={handleLogoutClick}>
              <img src="/images/logout.png" alt="Logout" />
              <span>Logout</span>
            </button>

            <div className="user-profile-desktop">
              <img
                src={avatarSrc}
                alt={displayName}
                className="user-avatar-small"
              />
            </div>

            <TutorsLinksLogo />
          </div>
        </aside>
      ) : null}

      {/* Mobile Drawer */}
      <aside className={`mobile-drawer ${isOpen ? "open" : ""}`}>
        <div className="drawer-header">
          {isAuthenticated ? (
            <div className="user-profile">
              <img src={avatarSrc} alt={displayName} className="user-avatar" />
              <div>
                <h3>{displayName}</h3>
                <p className="user-role-mobile">{userRole}</p>
              </div>
            </div>
          ) : (
            <div className="guest-header">
              <h3>Welcome Guest</h3>
            </div>
          )}
          <button className="close-btn" onClick={onToggle}>
            ×
          </button>
        </div>

        {isAuthenticated && mobileMenuItems.length > 0 && (
          <nav className="drawer-menu">
            {mobileMenuItems.map((item) => {
              const active = isActive(item.path);
              return (
                <button
                  key={item.label}
                  className={`drawer-item ${active ? "active" : ""}`}
                  onClick={() => {
                    if (item.isNotification) {
                      // Open notification panel instead of navigating
                      onToggle();
                      setTimeout(() => onOpenNotifications?.(), 200);
                    } else if (item.path) {
                      navigate(item.path);
                      onToggle(); // Close sidebar
                    }
                  }}
                >
                  <img
                    src={item.icon}
                    alt={item.label}
                    className="drawer-icon"
                  />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        )}

        <div className="drawer-footer guest-links">
          {[
            { label: "About Us", path: "/about-us" },
            { label: "Contact Us", path: "/get-help" },
            { label: "Terms & Conditions", path: "/terms-and-conditions" },
            { label: "Privacy Policy", path: "/privacy-policy" },
          ].map((link) => (
            <button
              key={link.label}
              className="footer-link"
              onClick={() => {
                navigate(link.path);
                onToggle();
              }}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="drawer-auth-section">
          <AuthSection />
        </div>

        <div className="sidebar-logo-container">
          <TutorsLinksLogo />
        </div>
      </aside>

      {/* Overlay */}
      {!isDesktop && isOpen && (
        <div className="sidebar-overlay" onClick={onToggle} />
      )}

      <ConfirmModal
        isOpen={showLogoutModal}
        title="Logout"
        message="Are you sure you want to logout?"
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
      />
    </>
  );
};

export default Sidebar;
